/**
 * AI 写作数据服务
 */

import type { AIWritingCard, TranslationCard, ArgumentCard, TopicInfo, PracticeMode } from './types';

let cachedCards: AIWritingCard[] | null = null;

// 加载卡片数据
export async function loadAIWritingCards(): Promise<AIWritingCard[]> {
  if (cachedCards) return cachedCards;
  
  const url = new URL('/ielts_data/writing/aiWritingCards.json', location.origin).toString();
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to load AI writing cards');
  
  cachedCards = await res.json();
  return cachedCards!;
}

// 获取所有话题
export async function getTopics(): Promise<TopicInfo[]> {
  const cards = await loadAIWritingCards();
  const topicMap = new Map<string, TopicInfo>();
  
  for (const card of cards) {
    const key = card.topic;
    if (!topicMap.has(key)) {
      topicMap.set(key, {
        en: card.topic,
        cn: card.topicCn,
        translationCount: 0,
        argumentCount: 0,
      });
    }
    const info = topicMap.get(key)!;
    if (card.type === 'translation') info.translationCount++;
    else if (card.type === 'argument') info.argumentCount++;
  }
  
  return Array.from(topicMap.values());
}

// 获取指定话题的卡片
export async function getCardsByTopic(topic: string, mode?: PracticeMode): Promise<AIWritingCard[]> {
  const cards = await loadAIWritingCards();
  return cards.filter(c => 
    c.topic === topic && (!mode || c.type === mode)
  );
}

// 获取随机卡片
export async function getRandomCard(mode?: PracticeMode): Promise<AIWritingCard | null> {
  const cards = await loadAIWritingCards();
  const filtered = mode ? cards.filter(c => c.type === mode) : cards;
  if (filtered.length === 0) return null;
  return filtered[Math.floor(Math.random() * filtered.length)];
}

// 获取指定话题的随机卡片
export async function getRandomCardByTopic(topic: string, mode?: PracticeMode): Promise<AIWritingCard | null> {
  const cards = await getCardsByTopic(topic, mode);
  if (cards.length === 0) return null;
  return cards[Math.floor(Math.random() * cards.length)];
}

// 类型守卫
export function isTranslationCard(card: AIWritingCard): card is TranslationCard {
  return card.type === 'translation';
}

export function isArgumentCard(card: AIWritingCard): card is ArgumentCard {
  return card.type === 'argument';
}
